import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import DealsPage from './pages/DealsPage';
import PicturesPage from './pages/PicturesPage';
import AdminPanel from './pages/AdminPanel';
import ManagerPanel from './pages/ManagerPanel';
import ClientPanel from './pages/ClientPanel';
import ReportPage from './pages/ReportPage';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';

// PWA компонент для регистрации Service Worker
const PWAUpdater: React.FC = () => {
  useEffect(() => {
    // Регистрируем Service Worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').then(
        (registration) => {
          console.log('SW registered: ', registration);
          
          // Проверяем обновления
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // Показываем уведомление об обновлении
                  if (confirm('Доступна новая версия приложения. Обновить?')) {
                    window.location.reload();
                  }
                }
              });
            }
          });
        },
        (registrationError) => {
          console.log('SW registration failed: ', registrationError);
        }
      );
    }
  }, []);

  return null;
};

// Компонент для защищенных маршрутов
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AllowedRolesRoute: React.FC<{
  children: React.ReactNode;
  allowed: Array<'admin' | 'manager' | 'client' | 'salary'>;
}> = ({ children, allowed }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!allowed.includes(user.type)) {
    if (user.type === 'salary') {
      return <Navigate to="/report" replace />;
    }
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

// Компонент для перенаправления на главную страницу роли
const RoleBasedRedirect: React.FC = () => {
  const { user } = useAuth();
  
  if (user?.type === 'admin') {
    return <Navigate to="/admin" replace />;
  } else if (user?.type === 'manager') {
    return <Navigate to="/manager" replace />;
  } else if (user?.type === 'client') {
    return <Navigate to="/client" replace />;
  } else if (user?.type === 'salary') {
    return <Navigate to="/report" replace />;
  } else {
    return <DashboardPage />;
  }
};

function App() {
  // Инициализация темы при загрузке
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      document.documentElement.setAttribute('data-theme', savedTheme);
    } else {
      // По умолчанию светлая тема
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, []);

  return (
    <Router>
      <AuthProvider>
        <PWAUpdater />
        <div className="min-h-screen bg-base-200">
          <Routes>
            {/* Публичные маршруты */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* Защищенные маршруты */}
            <Route path="/" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<RoleBasedRedirect />} />
              <Route
                path="deals"
                element={
                  <AllowedRolesRoute allowed={['admin']}>
                    <DealsPage />
                  </AllowedRolesRoute>
                }
              />
              <Route
                path="pictures"
                element={
                  <AllowedRolesRoute allowed={['admin']}>
                    <PicturesPage />
                  </AllowedRolesRoute>
                }
              />
              <Route
                path="admin"
                element={
                  <AllowedRolesRoute allowed={['admin']}>
                    <AdminPanel />
                  </AllowedRolesRoute>
                }
              />
              <Route
                path="report"
                element={
                  <AllowedRolesRoute allowed={['admin', 'salary']}>
                    <ReportPage />
                  </AllowedRolesRoute>
                }
              />
              <Route
                path="manager"
                element={
                  <AllowedRolesRoute allowed={['manager']}>
                    <ManagerPanel />
                  </AllowedRolesRoute>
                }
              />
              <Route
                path="client"
                element={
                  <AllowedRolesRoute allowed={['client']}>
                    <ClientPanel />
                  </AllowedRolesRoute>
                }
              />
            </Route>
            
            {/* Редирект на главную */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
